import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function LouderAppSwitcher({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const tools = [
    {
      id: 'hub',
      name: 'Louder Hub',
      path: '/hub',
      icon: '⚡',
      badge: 'Launchpad',
      desc: 'Central Suite Overview'
    },
    {
      id: 'mailer',
      name: 'Louder Mailer Studio',
      path: '/dashboard',
      icon: '✉️',
      badge: 'Active',
      desc: 'EDM & Newsletter Builder'
    },
    {
      id: 'meta-access',
      name: 'Meta Access Hub',
      path: '/meta-access',
      icon: '🔗',
      badge: 'Leads Tool',
      desc: '1-Link Meta Business Manager'
    }
  ];

  const currentTool = tools.find(t => location.pathname.startsWith(t.path)) || tools[0];

  const handleLogout = () => {
    localStorage.removeItem('louder_session');
    if (onLogout) onLogout();
    navigate('/login');
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 24px',
      backgroundColor: '#ffffff',
      borderBottom: '1px solid rgba(15, 9, 28, 0.08)',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
    }}>
      {/* Left: Suite Logo & App Switcher Dropdown */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div 
          onClick={() => navigate('/hub')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            fontWeight: '800',
            fontSize: '15px',
            letterSpacing: '0.05em',
            color: '#1f2937'
          }}
        >
          <span style={{
            background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
            color: '#ffffff',
            padding: '4px 8px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: '900'
          }}>
            LOUDER
          </span>
          <span style={{ color: '#5b6275', fontSize: '13px', fontWeight: '500' }}>TOOLS SUITE</span>
        </div>

        {/* Separator */}
        <div style={{ width: '1px', height: '20px', backgroundColor: 'rgba(15, 9, 28, 0.1)' }} />

        {/* App Switcher Dropdown Button */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 14px',
              backgroundColor: 'rgba(15, 9, 28, 0.03)',
              border: '1px solid rgba(15, 9, 28, 0.08)',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              color: '#1f2937',
              transition: 'all 0.2s ease'
            }}
          >
            <span>{currentTool.icon}</span>
            <span>{currentTool.name}</span>
            <span style={{ fontSize: '10px', color: '#9ca3af', marginLeft: '4px' }}>▼</span>
          </button>

          {/* Switcher Dropdown Menu */}
          {isOpen && (
            <div 
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: 0,
                width: '280px',
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                border: '1px solid rgba(15, 9, 28, 0.1)',
                boxShadow: '0 12px 36px rgba(0,0,0,0.12)',
                padding: '8px',
                zIndex: 200,
                animation: 'fadeIn 0.15s ease'
              }}
            >
              <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Louder Business Tools
              </div>

              {tools.map(tool => {
                const isActive = location.pathname.startsWith(tool.path);
                return (
                  <div
                    key={tool.id}
                    onClick={() => {
                      setIsOpen(false);
                      navigate(tool.path);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      backgroundColor: isActive ? 'rgba(217, 119, 6, 0.08)' : 'transparent',
                      border: isActive ? '1px solid rgba(217, 119, 6, 0.2)' : '1px solid transparent',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={e => {
                      if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(15, 9, 28, 0.04)';
                    }}
                    onMouseLeave={e => {
                      if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>{tool.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: '700', fontSize: '13px', color: '#1f2937' }}>
                          {tool.name}
                        </span>
                        <span style={{
                          fontSize: '10px',
                          padding: '2px 6px',
                          borderRadius: '6px',
                          backgroundColor: isActive ? '#d97706' : 'rgba(15, 9, 28, 0.06)',
                          color: isActive ? '#ffffff' : '#5b6275',
                          fontWeight: '600'
                        }}>
                          {tool.badge}
                        </span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                        {tool.desc}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Right: User profile, Hub quick link, and Logout */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          onClick={() => navigate('/hub')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#5b6275',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '600'
          }}
        >
          ⚡ All Tools
        </button>

        {user && user.role === 'admin' && (
          <button
            onClick={() => navigate('/admin')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              backgroundColor: 'rgba(217, 119, 6, 0.08)',
              border: '1px solid rgba(217, 119, 6, 0.2)',
              borderRadius: '10px',
              color: '#d97706',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '700'
            }}
          >
            👥 Team & Permissions
          </button>
        )}

        {user && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '4px 10px',
            backgroundColor: 'rgba(15, 9, 28, 0.03)',
            borderRadius: '20px',
            border: '1px solid rgba(15, 9, 28, 0.06)'
          }}>
            <div style={{
              width: '22px',
              height: '22px',
              borderRadius: '50%',
              backgroundColor: '#d97706',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: '700'
            }}>
              {(user.name || user.email || 'U').charAt(0).toUpperCase()}
            </div>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>
              {user.name || user.email}
            </span>
          </div>
        )}

        <button
          onClick={handleLogout}
          style={{
            padding: '6px 12px',
            backgroundColor: 'rgba(239, 68, 68, 0.06)',
            color: '#dc2626',
            border: '1px solid rgba(239, 68, 68, 0.15)',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '600',
            transition: 'all 0.15s ease'
          }}
        >
          Logout
        </button>
      </div>
    </div>
  );
}
